import React, { FC, useEffect, useState, useMemo, Fragment } from 'react'
import { observer } from 'mobx-react'
import { menuMobx, cartMobx, restaurantConfig, storeMobx } from 'restaurant'
import { buyTogetherMobx, decorationMobx, themeMobx, advertMobx } from '@mobxStore'
import productStyleController from '@ctrls/productStyleController'
import { debounce as _debounce, filter, forEach, sortBy } from '@dt/fe-utils/dist/lodash'
import { Empty, SoldoutFoldBar, WaterfallSkeleton } from '@cps'
import { ProductItem, BTAndCouponBar, WaterfallAdvertItem } from '../../'
import { deepClone } from '@dt/fe-utils'
import './index.scss'

import noMealImg from '@img/no-meal.png'

interface IProps {
  lazy?: boolean;
  products?: IViewProduct[];
  selectCategoryNo?: string;
}

// 存储计算结果
let lineToResult = {}
/** 广告类型 */
const advertType = 'waterfall'
/** 瀑布流计算定时器 */
let waterfallTimer
/** 瀑布流计算超过5次停掉 */
let calcStep = 0

const WaterfallTemplate: FC<IProps> = ({ lazy = true, products: _products, selectCategoryNo }) => {
  const isBuyTogether = !!Config.buyTogetherId
  const [products, setProducts] = useState([])
  const [productStyleMap, setProductStyleMap] = useState({} as {
    [no: string]: {
      left: number;
      top: number;
    };
  })
  const [height, setHeight] = useState(420)
  const [waterfallAdvertMap, setWaterfallAdvertMap] = useState({})
  const { advertInfo, availableAdsMap } = advertMobx
  const advert = advertInfo[advertType] || null
  const advertAdList = availableAdsMap[advertType] || []
  const soldoutCnt = filter(_products, (item) => item.disable).length
  const showSoldout = useMemo(() => {
    return menuMobx.openFoldSoldout === 1 && !!_products.find(item => item.disable)
  }, [_products])
  const displayMode = 'waterfall'

  const hashProduct = useMemo(() => {
    const channelEatType = storeMobx.info.storeId + restaurantConfig.mpc.channel + restaurantConfig.mpc.eatType + menuMobx.selectCategoryNo
    return channelEatType + products.map(p => p.no).join('')
  }, [products])

  /** 瀑布流插入需要计算的数据 */
  const calcSoltItem = () => {
    let calcItems = deepClone(products)
    // 插入数组后，index会偏移
    let offsetIndex = 0
    Object.keys(waterfallAdvertMap).forEach((key) => {
      waterfallAdvertMap?.[key]?.forEach((item, j) => {
        const i = Number(key)
        calcItems.splice(i + offsetIndex, 0, { no: `advert_${key === 'null' ? 0 : key}_${j}` })
        offsetIndex++
      })
    })
    calcItems.push({ no: `soldout_bar_${menuMobx.selectCategoryNo || 'all'}` })
    return calcItems as IViewProduct[]
  }
  const calcWaterfall = () => {
    clearTimeout(waterfallTimer)
    if (displayMode !== restaurantConfig.displayMode) return
    const productsSolts = calcSoltItem()
    const payload = productStyleController.waterfall(productsSolts, lazy)
    if (payload) {
      const { styleMap, height } = payload
      lineToResult[hashProduct] = payload
      setProductStyleMap(styleMap)
      setHeight(height)
    } else {
      if (calcStep <= 5) {
        setProductStyleMap({})
        waterfallTimer = setTimeout(() => {
          calcWaterfall()
        }, 300)
        ++calcStep
      }
    }
  }
  /** 瀑布流广告 */
  const setMenuWaterfallAdvert = () => {
    const adLen = advertAdList?.length
    const prodLen = _products?.length
    const queueType = advert?.extInfo?.queueType || 'auto'
    const queueArr = advert?.extInfo?.queueData?.[queueType]
    const advertKeys = (queueType === 'auto'
      ? (queueArr || []).slice(0, adLen)
      : advertAdList?.map((item, index) => {
        return (item.wfQueue >= 0 && item.wfQueue) || (queueArr?.[index] >= 0 && queueArr?.[index]) || 0
      }) || [])
    const advertMap: { [key: string]: any[] } = {}
    forEach(advertKeys, (item: number, index: number) => {
      if (item >= 0) {
        let key = String(item)
        // 当如果添加key超过商品总数，就放到最后
        if (item >= prodLen) {
          key = String(prodLen)
        }
        advertMap[key] = [
          ...advertMap[key] || [],
          advertAdList[index]
        ]
      }
    })
    setWaterfallAdvertMap(advertMap)
  }
  useEffect(() => {
    setMenuWaterfallAdvert()
    if (products.length && hashProduct) {
      if (lineToResult[hashProduct]) {
        setProductStyleMap(lineToResult[hashProduct].styleMap)
        setHeight(lineToResult[hashProduct].height)
      } else {
        calcStep = 0
        setProductStyleMap({})
        setTimeout(() => {
          calcWaterfall()
        }, 300)
      }
    }
  }, [products, hashProduct, advertAdList])

  useEffect(() => {
    setMenuWaterfallAdvert()
    if (_products.length && hashProduct) {
      if (lineToResult[hashProduct]) {
        setProductStyleMap(lineToResult[hashProduct].styleMap)
        setHeight(lineToResult[hashProduct].height)
      } else {
        setTimeout(() => {
          calcWaterfall()
        }, 300)
      }
    }
  }, [products, hashProduct, advertAdList, menuMobx.foldSoldoutMap, menuMobx.selectCategoryNo])

  useEffect(() => {
    setMenuWaterfallAdvert()
    setProducts(productStyleController.setProductImgHeight(filter(!menuMobx.openFoldSoldout ? deepClone(_products) : sortBy(deepClone(_products), (item) => item.disable ? 1 : 0), (item) => {
      // 未售罄或者 售罄并且开启折叠售罄并且展开
      return !item.disable || !menuMobx.openFoldSoldout || menuMobx.foldSoldoutMap?.[menuMobx.selectCategoryNo || 'all'] === true
    })))
  }, [_products, menuMobx.foldSoldoutMap])

  useEffect(() => {
    if (restaurantConfig.displayMode !== 'waterfall') {
      clearTimeout(waterfallTimer)
    }
    return () => {
      productStyleController.clearWaterfallData()
      clearTimeout(waterfallTimer)
    }
  }, [restaurantConfig.displayMode])

  useEffect(() => {
    lineToResult = {}
    productStyleController.clearWaterfallData()
    calcStep = 0
  }, [])

  return (
    <>
      {
        menuMobx.isLoading ? <WaterfallSkeleton /> : _products.length ? ( // 且有餐品
          <div className='product-list-content'>
            <BTAndCouponBar />
            {/* <p className='title'>{selectCategoryNo ? productMobx.categoryNoToCategoryMap[selectCategoryNo].name : i18n.t('common.meals')}</p> */}
            <div className='waterfall-template' style={{ height }}>
              {!!_products.length && waterfallAdvertMap?.[0]?.map((item, j) => (
                <WaterfallAdvertItem
                  wrapWidth={window.innerWidth}
                  key={`advert_0_${j}`}
                  data={item}
                  style={productStyleMap[`advert_0_${j}`]}
                  type='waterfall'
                  id={`advert_0_${j}`}
                />
              ))}
              {
                products.map((product, i) => {
                  const count = isBuyTogether ? buyTogetherMobx.productNoToCntMapForMenu?.[product.no] || 0 : cartMobx.cartResult?.productNoToCntMap?.[product.no] || 0
                  return (
                    <Fragment key={i}>
                      <ProductItem
                        mealDecorates={decorationMobx.mealDecorates || {} as TMealCommonDecorates}
                        style={productStyleMap[product.no] || { visibility: 'hidden' }}
                        key={product.no}
                        product={product}
                        count={count}
                        lazy={lazy}
                        backgroundMode={themeMobx.data.backgroundMode}
                        imgLoaded={menuMobx.imgLoadingMap[product.no]}
                      />
                      {!!_products.length && waterfallAdvertMap?.[i + 1]?.map((item, j) => (
                        <WaterfallAdvertItem
                          wrapWidth={window.innerWidth}
                          key={`advert_${i + 1}_${j}`}
                          data={item}
                          style={productStyleMap[`advert_${i + 1}_${j}`] || { visibility: 'hidden' }}
                          type='waterfall'
                          id={`advert_${i + 1}_${j}`}
                        />))}
                    </Fragment>
                  )
                })
              }
              {!!showSoldout && (
                <SoldoutFoldBar
                  cnt={soldoutCnt}
                  className='product-item'
                  show={menuMobx.foldSoldoutMap?.[menuMobx.selectCategoryNo || 'all'] === true}
                  no={menuMobx.selectCategoryNo || 'all'}
                  from='menu'
                  style={productStyleMap?.[`soldout_bar_${menuMobx.selectCategoryNo || 'all'}`] || { visibility: 'hidden' }}
                />
              )}
            </div>
          </div>
        ) : ( // 无餐品
          <Empty
            msg={themeMobx?.data?.abnormalStateSetting?.[0]?.subName || ''}
            iconType='dicon'
            iconName={themeMobx?.data?.abnormalStateSetting[0]?.icon}
            picUrl={themeMobx?.data?.abnormalStateSetting[0]?.pic || noMealImg}
            height='55vh'
          />
        )
      }
    </>
  )
}
export default observer(WaterfallTemplate)
